import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const storedCreds = localStorage.getItem('ssl_monitor_auth');
    if (storedCreds) {
      try {
        const parsed = JSON.parse(storedCreds);
        setCredentials(parsed);
        setIsAuthenticated(true);

        // Verify credentials and refresh role in background
        const authHeader = btoa(`${parsed.username}:${parsed.password}`);
        fetch(`${API_URL}/statistics`, {
          headers: {
            'Authorization': `Basic ${authHeader}`,
            'Content-Type': 'application/json'
          }
        })
          .then((res) => {
            if (res.ok) return res.json();
            if (res.status === 401) logout();
          })
          .then((data) => {
            if (data?.user?.role) {
              const updated = { ...parsed, role: data.user.role };
              setCredentials(updated);
              localStorage.setItem('ssl_monitor_auth', JSON.stringify(updated));
            }
          })
          .catch((err) => {
            console.error('Session validation error:', err);
          });
      } catch (error) {
        console.error('Failed to parse stored credentials:', error);
        localStorage.removeItem('ssl_monitor_auth');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      // Verify credentials by making a test API call
      const authHeader = btoa(`${username}:${password}`);
      
      const response = await fetch(`${API_URL}/statistics`, {
        headers: {
          'Authorization': `Basic ${authHeader}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const role = data.user?.role || (username.toLowerCase().includes('viewer') || username.toLowerCase().includes('readonly') ? 'readonly' : 'admin');
        // Store credentials
        const creds = { username, password, role };
        setCredentials(creds);
        setIsAuthenticated(true);
        localStorage.setItem('ssl_monitor_auth', JSON.stringify(creds));
        return { success: true, role };
      } else if (response.status === 401) {
        return { success: false, error: 'Invalid username or password' };
      } else {
        return { success: false, error: 'Authentication failed. Please try again.' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please check your connection.' };
    }
  };

  const logout = () => {
    setCredentials(null);
    setIsAuthenticated(false);
    localStorage.removeItem('ssl_monitor_auth');
  };

  const getAuthHeaders = () => {
    if (!credentials) {
      return {
        'Content-Type': 'application/json'
      };
    }
    
    const authHeader = btoa(`${credentials.username}:${credentials.password}`);
    return {
      'Authorization': `Basic ${authHeader}`,
      'Content-Type': 'application/json'
    };
  };

  const isReadOnly = credentials?.role === 'readonly';
  const isAdmin = !isReadOnly;

  const value = {
    isAuthenticated,
    isLoading,
    credentials,
    user: credentials,
    role: credentials?.role || (isReadOnly ? 'readonly' : 'admin'),
    isReadOnly,
    isAdmin,
    login,
    logout,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
